import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, FileText, Gift, LogOut, MessageCircle, Pizza, QrCode, Receipt, Settings, Shield, Star, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AppButton, Card, Field, Segmented } from "@/components/AppUI";
import { ShareButton } from "@/components/ShareButton";
import { LOGO } from "@/pages/MenuPage";
import { formatError, papi } from "@/lib/pizzeria";

const muted = { color: "var(--muted-c)" };

const AuthForm = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", phone: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.email.trim() || !f.password) return setError("Inserisci email e password");
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return setError("Inserisci un'email valida");
    if (mode === "register" && !f.name.trim()) return setError("Inserisci il tuo nome");
    if (mode === "register" && f.phone.trim() && f.phone.replace(/\D/g, "").length < 6) return setError("Inserisci un numero di telefono valido");
    if (mode === "register" && f.password.length < 6) return setError("La password deve avere almeno 6 caratteri");
    setError("");
    setLoading(true);
    try {
      const u = mode === "login" ? await login(f.email.trim(), f.password) : await register({ email: f.email.trim(), password: f.password, name: f.name.trim(), phone: f.phone.trim() });
      toast.success(`Benvenuto, ${u?.name || ""}`);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-4 space-y-3" testId="auth-form">
      <p className="text-base font-medium">Accedi per un'esperienza migliore</p>
      <p className="text-sm" style={muted}>Ritrova i tuoi ordini su ogni dispositivo. Puoi anche ordinare come ospite.</p>
      <Segmented testIdPrefix="auth-mode" value={mode} onChange={setMode} options={[{ key: "login", label: "Accedi" }, { key: "register", label: "Registrati" }]} />
      {mode === "register" && <><Field label="Nome" testId="auth-name-input" value={f.name} onChange={set("name")} placeholder="Mario Rossi" /><Field label="Telefono" testId="auth-phone-input" value={f.phone} onChange={set("phone")} placeholder="333 1234567" type="tel" /></>}
      <Field label="Email" testId="auth-email-input" value={f.email} onChange={set("email")} placeholder="nome@email.it" type="email" autoComplete="email" autoCapitalize="none" />
      <Field label="Password" testId="auth-password-input" value={f.password} onChange={set("password")} placeholder="••••••" type="password" />
      {error && <p className="text-sm" style={{ color: "var(--brand)" }} data-testid="auth-error">{error}</p>}
      <AppButton title={mode === "login" ? "Accedi" : "Crea account"} loading={loading} onClick={submit} testId="auth-submit-button" className="w-full" />
    </Card>
  );
};

const Loyalty = () => {
  const [l, setL] = useState(null);
  useEffect(() => {
    papi.get("/loyalty").then(({ data }) => setL(data)).catch(() => {});
  }, []);
  if (!l) return null;
  const target = l.target || 10;
  const progress = l.progress || 0;
  return (
    <div className="rounded-[20px] p-4 space-y-2" style={{ background: "var(--surface-inverse)", color: "var(--on-inverse)" }} data-testid="loyalty-card">
      <div className="flex items-center gap-2"><Gift className="h-[22px] w-[22px]" style={{ color: "var(--brand)" }} /><p className="text-base font-medium">Carta fedeltà 400°</p></div>
      <p className="text-xs opacity-70">Ogni 10 ordini ritirati, una pizza in omaggio</p>
      <div className="flex flex-wrap gap-2 my-1">
        {Array.from({ length: target }).map((_, i) => (
          <span key={i} className="h-11 w-11 rounded-full grid place-items-center" style={i < progress ? { background: "var(--brand)", border: "1px solid var(--brand)" } : { border: "1px solid rgba(255,255,255,0.25)" }} data-testid={`loyalty-stamp-${i}`}>
            <Pizza className="h-4 w-4" style={{ opacity: i < progress ? 1 : 0.3 }} />
          </span>
        ))}
      </div>
      <p className="text-sm" data-testid="loyalty-status">{l.available > 0 ? `${l.available} pizza in omaggio pronta! Verrà applicata al prossimo ordine.` : `${progress}/${target} ordini · ancora ${target - progress} per la pizza gratis`}</p>
    </div>
  );
};

const Reviews = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    papi.get("/feedback").then(({ data }) => setItems(data.filter((f) => !/^test/i.test(f.comment || "") && !/^test/i.test(f.customer_name || "")))).catch(() => setItems([]));
  }, []);
  if (!items) return null;
  return (
    <div className="space-y-3">
      <p className="text-base font-medium">Cosa dicono di noi</p>
      {!items.length && <p className="text-sm" style={muted}>Ancora nessuna recensione. Sii il primo!</p>}
      {items.slice(0, 10).map((f) => (
        <div key={f.id} className="rounded-xl p-3 space-y-1.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)" }} data-testid={`public-review-${f.id}`}>
          <div className="flex justify-between items-center"><p className="text-sm font-medium">{f.customer_name}</p><span className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="h-3.5 w-3.5" style={{ color: "#F59E0B", fill: n <= f.rating ? "#F59E0B" : "none" }} />)}</span></div>
          {f.comment && <p className="text-sm leading-5">{f.comment}</p>}
          {f.reply && <div className="rounded-md p-2" style={{ background: "var(--surface-3)", borderLeft: "3px solid var(--brand)" }}><p className="text-xs font-medium" style={{ color: "var(--brand)" }}>Risposta della pizzeria</p><p className="text-sm">{f.reply}</p></div>}
        </div>
      ))}
    </div>
  );
};

const RowLink = ({ to, icon: Icon, label, testId }) => (
  <Link to={to} className="press flex items-center gap-3 p-4 rounded-[20px]" style={{ background: "var(--surface-2)", border: "1px solid var(--border-c)" }} data-testid={testId}>
    <Icon className="h-5 w-5" style={{ color: "var(--brand)" }} /><span className="flex-1 font-medium">{label}</span><ChevronRight className="h-5 w-5" style={muted} />
  </Link>
);

export default function Profile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="flex-1 p-4 pb-6 space-y-6" data-testid="profile-page">
      <div className="flex items-center gap-3">
        <img src={LOGO} alt="Logo 400 Gradi" className="h-14 w-14 rounded-full object-cover" />
        <div className="flex-1"><h1 className="text-2xl font-medium">Profilo</h1><p className="text-sm" style={muted}>Pizzeria 400° Gradi · Masi dal 1983</p></div>
      </div>
      {user ? (
        <Card className="p-4 space-y-3" testId="profile-user-card">
          <div className="flex items-center gap-3">
            <span className="h-12 w-12 rounded-full grid place-items-center" style={{ background: "var(--brand-3)", color: "var(--brand)" }}><User className="h-6 w-6" /></span>
            <div className="min-w-0"><p className="text-xl font-medium truncate" data-testid="profile-user-name">{user.name}</p><p className="text-sm truncate" style={muted}>{user.email}</p></div>
          </div>
          <AppButton variant="outline" icon={Settings} title="Il mio account" onClick={() => nav("/account")} testId="profile-account-button" className="w-full" />
          <AppButton variant="ghost" icon={LogOut} title="Esci" onClick={logout} testId="logout-button" className="w-full" />
        </Card>
      ) : user === false && <AuthForm />}
      {user && <Loyalty />}
      <div className="space-y-3">
        <RowLink to="/ordini" icon={Receipt} label="I miei ordini" testId="profile-orders-link" />
        <RowLink to="/assistente" icon={MessageCircle} label="Assistente virtuale" testId="profile-assistant-link" />
        <RowLink to="/qr" icon={QrCode} label="QR code per tavoli e volantini" testId="profile-qr-link" />
        <ShareButton variant="outline" className="w-full h-[52px] rounded-full" />
      </div>
      <Reviews />
      <div className="flex justify-center gap-6 text-sm pb-2">
        <Link to="/legal/privacy" className="flex items-center gap-1" style={muted} data-testid="profile-privacy-link"><Shield className="h-4 w-4" />Privacy</Link>
        <Link to="/legal/terms" className="flex items-center gap-1" style={muted} data-testid="profile-terms-link"><FileText className="h-4 w-4" />Termini</Link>
      </div>
    </div>
  );
}
